import type { Segment, VoterCounts, VoterDataFieldDto } from '@signalvote/shared';
import { HouseholdMethod, assertNever } from '@signalvote/shared';
import type { HeldCounts, VoterHistory } from '@/hooks/queries/useVoterData';
import type { RuleKey, SegmentKey } from '@/lib/voterSegmentKey';
import { isVoteRule, sameArea, sameRule, segmentKey } from '@/lib/voterSegmentKey';

export enum Confidence {
  Exact = 'Exact',
  About = 'About',
  Range = 'Range',
}

export type Estimate =
  | { confidence: Confidence.Exact; value: number }
  | { confidence: Confidence.About; value: number }
  | { confidence: Confidence.Range; value: number; low: number; high: number };

export interface ListEstimate {
  voters: Estimate;
  households: Estimate;
  records: Estimate;
  priceCents: Estimate;
}

const RANGE_THRESHOLD_CENTS = 5000;

interface Totals {
  voters: number;
  households: number;
}

export function householdsOf(base: Totals, share: number, field: VoterDataFieldDto): number {
  switch (field.householdMethod) {
    case HouseholdMethod.Scaling:
      return base.households * share * field.householdFactor;
    case HouseholdMethod.HouseholdSize:
      return base.households === 0 ? 0 : base.households * (1 - (1 - share) ** (base.voters / base.households)) * field.householdFactor;
    default:
      return assertNever(field.householdMethod);
  }
}

export function baseHouseholdsOf(child: Totals, share: number, field: VoterDataFieldDto): number {
  switch (field.householdMethod) {
    case HouseholdMethod.Scaling:
      return child.households / (share * field.householdFactor);
    case HouseholdMethod.HouseholdSize: {
      const voters = child.voters / share;
      let low = child.households / field.householdFactor;
      let high = voters;
      if (householdsOf({ voters, households: high }, share, field) < child.households) return high;
      for (let i = 0; i < 60; i++) {
        const mid = (low + high) / 2;
        if (householdsOf({ voters, households: mid }, share, field) < child.households) low = mid;
        else high = mid;
      }
      return (low + high) / 2;
    }
    default:
      return assertNever(field.householdMethod);
  }
}

export enum StepKind {
  Keep = 'Keep',
  Scale = 'Scale',
  Unscale = 'Unscale',
}

/** Where a step's share was measured: on the whole area, or inside a list already narrowed by rules. */
type Measured = 'onArea' | 'narrowed';

/**
 * Nine in ten of a step's misses are within this factor, as |ln(predicted ÷ real)|, fitted on customer-like
 * walks counted by L2 (docs/experiments/2026-10-01-voter-estimate-calibration.md). A list's first Keep
 * reads its voters from the breakdown, so only its households miss.
 */
const VOTER_LOG_P90: Record<StepKind, Record<Measured, number>> = {
  [StepKind.Keep]: { onArea: 0.92, narrowed: 0.47 },
  [StepKind.Unscale]: { onArea: 0.85, narrowed: 0.48 },
  [StepKind.Scale]: { onArea: 0.07, narrowed: 0.07 },
};
const HOUSEHOLD_LOG_P90: Record<StepKind | 'FirstKeep', Record<Measured, number>> = {
  FirstKeep: { onArea: 0.19, narrowed: 0.11 },
  [StepKind.Keep]: { onArea: 0.77, narrowed: 0.42 },
  [StepKind.Unscale]: { onArea: 0.92, narrowed: 0.79 },
  [StepKind.Scale]: { onArea: 0.07, narrowed: 0.07 },
};

type Step = { field: VoterDataFieldDto; measured: Measured } & (
  | { kind: StepKind.Keep; voters: number; households: number | null }
  | { kind: StepKind.Scale; share: number }
  | { kind: StepKind.Unscale; share: number }
);

const measuredIn = (key: SegmentKey): Measured => (key.rules.length === 0 ? 'onArea' : 'narrowed');

interface Plan {
  base: HeldCounts;
  steps: Step[];
}

const stepCost = (step: Step) => (step.kind === StepKind.Keep ? 1 : 1.5);
const planCost = (plan: Plan) => plan.steps.reduce((sum, step) => sum + stepCost(step), 0);

const rulesByField = (key: SegmentKey) => new Map(key.rules.map((r) => [r.fieldId, r]));

function distance(a: SegmentKey, b: SegmentKey): number {
  const was = rulesByField(a);
  const is = rulesByField(b);
  return [...new Set([...was.keys(), ...is.keys()])].filter((id) => {
    const x = was.get(id);
    const y = is.get(id);
    return !x || !y || !sameRule(x, y);
  }).length;
}

function nearest<T extends { key: SegmentKey; landedAt: number }>(held: T[], to: SegmentKey): T | undefined {
  return held.reduce<T | undefined>((best, h) => {
    if (!best) return h;
    const d = distance(h.key, to) - distance(best.key, to);
    return d < 0 || (d === 0 && h.landedAt > best.landedAt) ? h : best;
  }, undefined);
}

function sumOf(breakdown: Record<string, number> | undefined, labels: string[]): number | null {
  if (!breakdown || !labels.every((label) => label in breakdown)) return null;
  return labels.reduce((sum, label) => sum + breakdown[label], 0);
}

function stepsFor(base: HeldCounts, field: VoterDataFieldDto, was: RuleKey | undefined, is: RuleKey | undefined, history: VoterHistory): Step[] | null {
  if (is && isVoteRule(is)) return voteSteps(base, field, was, is, history);
  if (was && isVoteRule(was)) return voteSteps(base, field, was, undefined, history);

  if (is && (!was || is.values.every((v) => was.values.includes(v)))) {
    const voters = sumOf(base.counts.breakdowns[field.id], is.values);
    return voters === null ? null : [{ kind: StepKind.Keep, field, measured: measuredIn(base.key), voters, households: null }];
  }
  if (!was) return null;

  const source = nearest(history.counts.filter((h) => sameArea(h.key, base.key) && !rulesByField(h.key).has(field.id)), base.key);
  if (!source || source.counts.total === 0) return null;
  const shareOf = (labels: string[]) => {
    const voters = sumOf(source.counts.breakdowns[field.id], labels);
    return voters === null ? null : voters / source.counts.total;
  };
  const before = shareOf(was.values);
  if (!before) return null;
  const measured = measuredIn(source.key);
  const steps: Step[] = [{ kind: StepKind.Unscale, field, measured, share: before }];
  if (is) {
    const after = shareOf(is.values);
    if (after === null) return null;
    steps.push({ kind: StepKind.Scale, field, measured, share: after });
  }
  return steps;
}

function voteSteps(base: HeldCounts, field: VoterDataFieldDto, was: RuleKey | undefined, is: RuleKey | undefined, history: VoterHistory): Step[] | null {
  const counted = history.votes.flatMap((v) =>
    v.fieldId === field.id && sameArea(v.key, base.key) && v.votes.voted !== null && v.votes.didNot !== null
      ? [{ key: v.key, landedAt: v.landedAt, voted: v.votes.voted, didNot: v.votes.didNot, votedHouseholds: v.votes.votedHouseholds }]
      : [],
  );
  if (!was && is && isVoteRule(is)) {
    const own = counted.find((v) => distance(v.key, base.key) === 0);
    if (!own) return null;
    return [{ kind: StepKind.Keep, field, measured: measuredIn(base.key), voters: is.voted ? own.voted : own.didNot, households: is.voted ? own.votedHouseholds : null }];
  }
  // Undo a vote rule only with counts taken on this very list without it, as its popover asks for.
  // Voting moves with turnout, age and party, so a share measured on another list missed several-fold
  // on live walks (docs/experiments/2026-10-01-voter-estimate-votes-walk.md).
  const source = counted.find((v) => distance(v.key, base.key) === 1);
  if (!source) return null;
  const all = source.voted + source.didNot;
  const shareOf = (voted: boolean) => (all === 0 ? 0 : (voted ? source.voted : source.didNot) / all);
  const before = was && isVoteRule(was) ? shareOf(was.voted) : 0;
  if (!before) return null;
  const measured = measuredIn(source.key);
  const steps: Step[] = [{ kind: StepKind.Unscale, field, measured, share: before }];
  if (is && isVoteRule(is)) steps.push({ kind: StepKind.Scale, field, measured, share: shareOf(is.voted) });
  return steps;
}

function planFrom(base: HeldCounts, target: SegmentKey, fieldOf: Map<string, VoterDataFieldDto>, history: VoterHistory): Plan | null {
  const was = rulesByField(base.key);
  const is = rulesByField(target);
  const steps: Step[] = [];
  for (const fieldId of new Set([...was.keys(), ...is.keys()])) {
    const a = was.get(fieldId);
    const t = is.get(fieldId);
    if (a && t && sameRule(a, t)) continue;
    const field = fieldOf.get(fieldId);
    const change = field && stepsFor(base, field, a, t, history);
    if (!change) return null;
    steps.push(...change);
  }
  return { base, steps };
}

interface Guess {
  value: number;
  logP90: number;
}

const clamp = (households: number, low: number, high: number) => Math.min(Math.max(low, households), high);
const combined = (errors: number[]) => Math.sqrt(errors.reduce((sum, e) => sum + e * e, 0));

function follow({ base, steps }: Plan): { voters: Guess; households: Guess } {
  let voters = base.counts.total;
  let households = base.counts.households;
  const voterErrors: number[] = [];
  const householdErrors: number[] = [];
  steps.forEach((step, i) => {
    const from = { voters, households };
    switch (step.kind) {
      case StepKind.Keep: {
        const share = base.counts.total === 0 ? 0 : step.voters / base.counts.total;
        voters = i === 0 ? step.voters : voters * share;
        if (i > 0) voterErrors.push(VOTER_LOG_P90[step.kind][step.measured]);
        if (i === 0 && steps.length === 1 && step.households !== null) households = step.households;
        else {
          households = clamp(householdsOf(from, share, step.field), 1, Math.min(voters, from.households));
          householdErrors.push(HOUSEHOLD_LOG_P90[i === 0 ? 'FirstKeep' : step.kind][step.measured]);
        }
        break;
      }
      case StepKind.Scale:
        voters *= step.share;
        households = clamp(householdsOf(from, step.share, step.field), 1, Math.min(voters, from.households));
        voterErrors.push(VOTER_LOG_P90[step.kind][step.measured]);
        householdErrors.push(HOUSEHOLD_LOG_P90[step.kind][step.measured]);
        break;
      case StepKind.Unscale:
        voters /= step.share;
        households = clamp(baseHouseholdsOf(from, step.share, step.field), from.households, voters);
        voterErrors.push(VOTER_LOG_P90[step.kind][step.measured]);
        householdErrors.push(HOUSEHOLD_LOG_P90[step.kind][step.measured]);
        break;
      default:
        return assertNever(step);
    }
  });
  if (voters === 0) households = 0;
  return {
    voters: { value: Math.round(voters), logP90: combined(voterErrors) },
    households: { value: Math.round(households), logP90: voters === 0 ? 0 : combined(householdErrors) },
  };
}

function toEstimate({ value, logP90 }: Guess, pricePerRecordCents: number): Estimate {
  if (logP90 === 0) return { confidence: Confidence.Exact, value };
  const high = value * Math.exp(logP90);
  if ((high - value) * pricePerRecordCents <= RANGE_THRESHOLD_CENTS) return { confidence: Confidence.About, value };
  return { confidence: Confidence.Range, value, low: Math.round(value / Math.exp(logP90)), high: Math.round(high) };
}

/** The most an estimate could be: a range's top, otherwise its value. */
export function highestOf(estimate: Estimate): number {
  return estimate.confidence === Confidence.Range ? estimate.high : estimate.value;
}

/** An estimate scaled by a unit price or count, keeping its confidence. */
export function times(estimate: Estimate, factor: number): Estimate {
  switch (estimate.confidence) {
    case Confidence.Exact:
    case Confidence.About:
      return { ...estimate, value: estimate.value * factor };
    case Confidence.Range:
      return { ...estimate, value: estimate.value * factor, low: estimate.low * factor, high: estimate.high * factor };
    default:
      return assertNever(estimate);
  }
}

/** A list is sold per voter, so its records are its voters. */
function listOf(voters: Estimate, households: Estimate, pricePerRecordCents: number): ListEstimate {
  return { voters, households, records: voters, priceCents: times(voters, pricePerRecordCents) };
}

export function exactList(counts: VoterCounts, pricePerRecordCents: number): ListEstimate {
  const exact = (value: number): Estimate => ({ confidence: Confidence.Exact, value });
  return listOf(exact(counts.total), exact(counts.households), pricePerRecordCents);
}

function bestPlan(history: VoterHistory, target: SegmentKey, fields: VoterDataFieldDto[]): Plan | null {
  const fieldOf = new Map(fields.map((f) => [f.id, f]));
  return history.counts
    .filter((h) => sameArea(h.key, target))
    .map((h) => planFrom(h, target, fieldOf, history))
    .reduce<Plan | null>((best, p) => {
      if (!p) return best;
      if (!best) return p;
      const d = planCost(p) - planCost(best);
      return d < 0 || (d === 0 && p.base.landedAt > best.base.landedAt) ? p : best;
    }, null);
}

/** The steps predictList takes from its chosen held answer, and whether each step's share was measured on the whole area. */
export function planSteps(history: VoterHistory, segment: Segment, fields: VoterDataFieldDto[]): { kind: StepKind; onArea: boolean }[] | null {
  return bestPlan(history, segmentKey(segment), fields)?.steps.map(({ kind, measured }) => ({ kind, onArea: measured === 'onArea' })) ?? null;
}

/** Null when nothing held can say: an area never counted, or a vote rule whose counts were never asked for. */
export function predictList(history: VoterHistory, segment: Segment, fields: VoterDataFieldDto[], pricePerRecordCents: number): ListEstimate | null {
  const plan = bestPlan(history, segmentKey(segment), fields);
  if (!plan) return null;

  const guessed = follow(plan);
  return listOf(toEstimate(guessed.voters, pricePerRecordCents), toEstimate(guessed.households, pricePerRecordCents), pricePerRecordCents);
}
